import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Container, Row, Col, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUserProfile, updateUserDetails } from '../../services/userService';
import Toast from '../../components/Toast';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const MAX_IMAGE_DIMENSION = 1024; // 1024px

const EditProfile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [intercom, setIntercom] = useState('');
  const [floor, setFloor] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [messagingId, setMessagingId] = useState('');

  useEffect(() => {
    const loadUserProfile = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      try {
        setLoading(true);
        const profile = await getUserProfile(currentUser.uid);
        if (profile) {
          setName(profile.name || '');
          setAddress(profile.address || '');
          setPhone(profile.phone || '');
          setCity(profile.city || '');
          setZipCode(profile.zipCode || '');
          setIntercom(profile.intercom || '');
          setFloor(profile.floor || '');
          setAvatarPreview(profile.avatar || '');
          setMessagingId(profile.messagingId || '');
        }
      } catch (err) {
        console.error('Errore nel caricamento del profilo:', err);
        setError('Errore nel caricamento del profilo');
        setToastMessage('Errore nel caricamento del profilo');
        setToastVariant('danger');
        setShowToast(true);
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [currentUser, navigate]);

  const validateImage = (file) => {
    return new Promise((resolve, reject) => {
      if (file.size > MAX_FILE_SIZE) {
        reject('L\'immagine non deve superare i 2MB');
        return;
      }

      const img = new Image();
      img.onload = () => {
        if (img.width > MAX_IMAGE_DIMENSION || img.height > MAX_IMAGE_DIMENSION) {
          reject(`L'immagine non deve superare i ${MAX_IMAGE_DIMENSION}x${MAX_IMAGE_DIMENSION} pixel`);
        } else {
          resolve();
        }
      };
      img.onerror = () => reject('Errore nel caricamento dell\'immagine');
      img.src = URL.createObjectURL(file);
    });
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        await validateImage(file);
        setAvatarFile(file);
        // Converti il file in base64
        const reader = new FileReader();
        reader.onloadend = () => {
          setAvatarPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        setToastMessage(error);
        setToastVariant('danger');
        setShowToast(true);
        e.target.value = ''; // Reset del campo file
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Usa direttamente l'immagine base64
      const avatarUrl = avatarPreview;

      await updateUserDetails(currentUser.uid, {
        name,
        address,
        phone,
        city,
        zipCode,
        intercom,
        floor,
        avatar: avatarUrl,
        messagingId,
        updatedAt: new Date().toISOString()
      });

      setToastMessage('Profilo aggiornato con successo');
      setToastVariant('success');
      setShowToast(true);
      setTimeout(() => navigate('/profile'), 1000);
    } catch (err) {
      console.error("Errore nel salvataggio del profilo:", err);
      setError('Errore durante il salvataggio');
      setToastMessage('Errore durante il salvataggio');
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  return (
    <Container className="py-4 d-flex justify-content-center">
      <Toast 
        show={showToast} 
        onClose={() => setShowToast(false)} 
        message={toastMessage}
        variant={toastVariant}
      />
      <Card className="p-4 shadow-sm border-0 rounded-4" style={{ maxWidth: 420, width: '100%' }}>
        <Card.Body>
          <h4 className="fw-bold mb-4">{t('profile.edit_profile')}</h4>
          <Form onSubmit={handleSubmit}>
            {/* Preview immagine profilo */}
            <div className="d-flex flex-column align-items-center mb-3">
              <div style={{ width: 90, height: 90, borderRadius: '50%', overflow: 'hidden', background: '#f2f2f2', marginBottom: 8 }}>
                {avatarPreview ? (
                  <img src={avatarPreview} alt="avatar preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <i className="bi bi-person-circle" style={{ fontSize: 90, color: '#e0e0e0' }}></i>
                )}
              </div>
              <Form.Group controlId="profileAvatarFile" className="w-100">
                <Form.Label className="small">{t('profile.avatar_upload')}</Form.Label>
                <Form.Control 
                  type="file" 
                  accept="image/*" 
                  onChange={handleAvatarChange}
                />
                <Form.Text className="text-muted">
                  Dimensioni massime: 2MB. Risoluzione massima: {MAX_IMAGE_DIMENSION}x{MAX_IMAGE_DIMENSION} pixel
                </Form.Text>
              </Form.Group>
            </div>
            <Form.Group className="mb-3" controlId="profileName">
              <Form.Label>{t('profile.name')}</Form.Label>
              <Form.Control
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="profilePhone">
              <Form.Label>{t('profile.phone')}</Form.Label>
              <Form.Control
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="profileMessagingId">
              <Form.Label>ID Wechat/Whatsapp</Form.Label>
              <Form.Control
                type="text"
                value={messagingId}
                onChange={e => setMessagingId(e.target.value)}
                placeholder="Inserisci il tuo ID Wechat o Whatsapp"
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="profileAddress">
              <Form.Label>{t('profile.delivery_address')}</Form.Label>
              <Form.Control
                type="text"
                value={address}
                onChange={e => setAddress(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="profileCity">
              <Form.Label>Città</Form.Label>
              <Form.Control
                type="text"
                value={city}
                onChange={e => setCity(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="profileZipCode">
              <Form.Label>CAP</Form.Label>
              <Form.Control
                type="text"
                value={zipCode}
                onChange={e => setZipCode(e.target.value)}
                placeholder="Inserisci il CAP"
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="profileIntercom">
              <Form.Label>Citofono</Form.Label>
              <Form.Control
                type="text"
                value={intercom}
                onChange={e => setIntercom(e.target.value)}
                placeholder="Inserisci il numero del citofono"
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="profileFloor">
              <Form.Label>Interno e Scala</Form.Label>
              <Form.Control
                type="text"
                value={floor}
                onChange={e => setFloor(e.target.value)}
                placeholder="Es: Scala A, Interno 5"
              />
            </Form.Group>
            {error && <div className="text-danger mb-3">{error}</div>}
            <Row>
              <Col xs={6}>
                <Button variant="outline-secondary" className="w-100 rounded-pill" onClick={() => navigate('/profile')} disabled={saving}>
                  {t('common.cancel')}
                </Button>
              </Col>
              <Col xs={6}>
                <Button type="submit" variant="primary" className="w-100 rounded-pill" disabled={saving}>
                  {saving ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    t('common.save')
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default EditProfile; 